/**
 * MYAC-1514 — the « Rapport de veille » page lists the project's reports with
 * where each one stands. A run being generated has no report doc yet (the v3
 * engine writes it last), so its row comes from its `regulation_watch` command;
 * a finished report's row from its own doc.
 *
 * Pure: the page and its tests read the same rows.
 */
import type { Command } from '@/models/Command'
import type { RegulationReport } from '@/models/RegulationReport'
import { anyToDate } from '@/models/Models'

/** A run still `in_progress` after this long is not running any more: the job
 *  died without marking its command (dev holds one since April 2026). The v3
 *  wait is 30 minutes at most; two hours leaves a slow list room. */
export const RUN_INTERRUPTED_AFTER_MS = 2 * 60 * 60 * 1000

export interface RunWait {
  identifying: number
  curating: number
  until: Date | null
}

export type RunStage = 'queued' | 'waiting' | 'reading' | 'writing'

export type FailureReason = 'insufficient_credits' | 'empty_list' | 'internal' | 'interrupted'

export type ReportStatus =
  | { key: 'finalised'; by: string | null; at: Date | null }
  | { key: 'to_review'; remaining: number; toReview: number }
  | { key: 'ready_to_finalise' }
  /** Left open while a newer report exists: history, not a report to act on
   *  (Aymeric, 03/10: an old open report must not read « Prêt à finaliser »). */
  | { key: 'superseded' }
  /** v1 and v2 reports: no review state on the report itself. */
  | { key: 'generated' }

export type ReportListRow =
  | { kind: 'running'; id: string; stage: RunStage; current: number; total: number; wait: RunWait | null }
  | { kind: 'failed'; id: string; at: Date | null; reason: FailureReason }
  | { kind: 'report'; id: string; report: RegulationReport; status: ReportStatus; latest: boolean }

function num(v: unknown): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : 0
}

function toDate(v: unknown): Date | null {
  if (v === null || v === undefined) return null
  return anyToDate({ obj: { v }, key: 'v' }) ?? null
}

/** The payload as Firestore sent it. `Command.payload` went through
 *  class-transformer, which empties a Timestamp nested in an untyped object
 *  (`waiting.until` arrived as an object with no seconds). */
function rawPayload(command: Command): Record<string, unknown> {
  const fromSnapshot = command.doc?.data()?.payload
  const payload = fromSnapshot && typeof fromSnapshot === 'object' ? fromSnapshot : command.payload
  return (payload ?? {}) as Record<string, unknown>
}

/** What the run's `payload.progress` says, read tolerantly (older runs carry
 *  no `waiting`, a newer service may send a step this front does not know). */
export function runProgress(command: Command): Pick<Extract<ReportListRow, { kind: 'running' }>, 'stage' | 'current' | 'total' | 'wait'> {
  if (command.status === 'requested') return { stage: 'queued', current: 0, total: 0, wait: null }
  const raw = rawPayload(command).progress
  const p = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {}
  const w = p.waiting && typeof p.waiting === 'object' ? (p.waiting as Record<string, unknown>) : null
  const wait = w && (num(w.identifying) > 0 || num(w.curating) > 0)
    ? { identifying: num(w.identifying), curating: num(w.curating), until: toDate(w.until) }
    : null
  const stage: RunStage = wait ? 'waiting' : p.step === 'generating' ? 'writing' : 'reading'
  return { stage, current: num(p.current), total: num(p.total), wait }
}

export function failureReason(command: Command): FailureReason {
  const raw = rawPayload(command).failure_reason
  return raw === 'insufficient_credits' || raw === 'empty_list' ? raw : 'internal'
}

function isInterrupted(command: Command, now: Date): boolean {
  return command.status === 'in_progress'
    && !!command.started_at
    && now.getTime() - command.started_at.getTime() > RUN_INTERRUPTED_AFTER_MS
}

/** A run is really going on: requested, or in progress and not interrupted.
 *  The page's « Générer » waits for it, and only for it. */
export function isRunLive(command: Command, now: Date = new Date()): boolean {
  return command.status === 'requested' || (command.status === 'in_progress' && !isInterrupted(command, now))
}

export function reportStatus(report: RegulationReport, latest = true): ReportStatus {
  if (report.schema_version !== 'v3') return { key: 'generated' }
  const f = report.finalisation
  if (f?.status === 'finalised') return { key: 'finalised', by: f.signatory_name, at: f.finalised_at }
  if (!latest) return { key: 'superseded' }
  const remaining = report.lines_remaining ?? 0
  if (remaining > 0) return { key: 'to_review', remaining, toReview: Math.max(report.lines_to_review ?? remaining, remaining) }
  return { key: 'ready_to_finalise' }
}

function reportTime(report: RegulationReport): number {
  return report.generated_at?.getTime() ?? report.created_at?.getTime() ?? 0
}

/**
 * The rows, newest first: runs being generated, then failures since the newest
 * report (an older failure was followed by a report that supersedes it), then
 * the reports. `reports` is expected newest first, as the store keeps it.
 */
export function reportListRows(reports: RegulationReport[], runs: Command[], now: Date = new Date()): ReportListRow[] {
  const newest = reports.length ? reportTime(reports[0]) : 0
  const running: ReportListRow[] = []
  const failed: ReportListRow[] = []
  for (const run of runs) {
    if (isRunLive(run, now)) {
      running.push({ kind: 'running', id: run.id, ...runProgress(run) })
      continue
    }
    const interrupted = isInterrupted(run, now)
    if (run.status !== 'failed' && !interrupted) continue
    const at = interrupted ? run.started_at : run.finished_at ?? run.started_at
    // Undated, or before the newest report: superseded by it.
    if (!at || at.getTime() <= newest) continue
    failed.push({ kind: 'failed', id: run.id, at, reason: interrupted ? 'interrupted' : failureReason(run) })
  }
  failed.sort((a, b) => ((b as { at: Date }).at.getTime() - (a as { at: Date }).at.getTime()))
  const latestId = reports[0]?.id
  return [
    ...running,
    ...failed,
    ...reports.map((report): ReportListRow => ({
      kind: 'report', id: report.id, report, status: reportStatus(report, report.id === latestId), latest: report.id === latestId,
    })),
  ]
}
